import React, { useMemo, useState } from 'react';
import { AopLink, Card, Confidence, InternalCard, Modal, Pill, StatusPill, WarnRow } from '../ui';
import { clusteringDiagnostics, clusterPoints, segments as clusterSegments, segmentIntelligence } from '../copData';
import { useCop } from '../CopApp';

const FUNNEL_STAGES = [
	'bounce',
	'product_abandonment',
	'active_cart_abandonment',
	'active_checkout_abandonment',
	'returning_inactive',
	'post_purchase',
];

const SOLUTION_FAMILIES = [
	'targeted_tactic',
	'coupon_corrector',
	'lead_capture',
	'email_remarketing',
	'product_recommendation',
];

const EMPTY_FORM = {
	segment_name: '',
	funnel_stage: 'active_cart_abandonment',
	recommended_solution_family: 'targeted_tactic',
	audience_size: '',
	avg_cart_value: '',
	conversion_rate: '',
	recommended_action: '',
};

function ClusterScatter() {
	const points = useMemo(() => clusterPoints(), []);
	const [hover, setHover] = useState(null);
	return (
		<div className="cop-scatter-wrap">
			<svg viewBox="0 0 100 56" width="100%" height="290" preserveAspectRatio="none">
				{points.map((p, i) => (
					<circle
						key={i}
						cx={p.x}
						cy={p.y * 0.56}
						r={hover === p.segment ? p.r * 0.62 : p.r * 0.5}
						fill={p.color}
						opacity={hover && hover !== p.segment ? 0.12 : 0.75}
						style={{ transition: 'opacity 0.2s ease, r 0.2s ease' }}
					/>
				))}
			</svg>
			<div style={{ position: 'absolute', left: 12, bottom: 8, fontSize: 9.5, color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>
				engagement depth →
			</div>
			<div
				style={{
					position: 'absolute', left: 8, top: 10, fontSize: 9.5, color: 'var(--text-dim)',
					fontFamily: 'var(--font-mono)', writingMode: 'vertical-rl', transform: 'rotate(180deg)',
				}}
			>
				purchase intent →
			</div>
			<div className="cop-scatter-legend" style={{ position: 'absolute', right: 14, top: 12, flexDirection: 'column', gap: 5 }}>
				{clusterSegments.map((s) => (
					<span
						key={s.segment_id}
						onMouseEnter={() => setHover(s.segment_name)}
						onMouseLeave={() => setHover(null)}
						style={{ cursor: 'default' }}
					>
						<span className="cop-legend-dot" style={{ color: s.color, background: s.color }} />
						{s.segment_name}
					</span>
				))}
			</div>
		</div>
	);
}

function SegmentForm({ form, setForm, error }) {
	const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
	return (
		<div className="cop-form-grid">
			<label className="cop-form-field cop-form-span">
				<span>Segment name *</span>
				<input className="cop-input" value={form.segment_name} onChange={set('segment_name')} placeholder="e.g. VIP Win-Back Cohort" />
			</label>
			<label className="cop-form-field">
				<span>Funnel stage *</span>
				<select className="cop-select" value={form.funnel_stage} onChange={set('funnel_stage')}>
					{FUNNEL_STAGES.map((s) => (
						<option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
					))}
				</select>
			</label>
			<label className="cop-form-field">
				<span>Solution family</span>
				<select className="cop-select" value={form.recommended_solution_family} onChange={set('recommended_solution_family')}>
					{SOLUTION_FAMILIES.map((s) => (
						<option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
					))}
				</select>
			</label>
			<label className="cop-form-field">
				<span>Audience size</span>
				<input className="cop-input" type="number" min="0" value={form.audience_size} onChange={set('audience_size')} placeholder="0" />
			</label>
			<label className="cop-form-field">
				<span>Avg cart value ($)</span>
				<input className="cop-input" type="number" min="0" value={form.avg_cart_value} onChange={set('avg_cart_value')} placeholder="0" />
			</label>
			<label className="cop-form-field">
				<span>Conversion rate (%)</span>
				<input className="cop-input" type="number" min="0" max="100" step="0.1" value={form.conversion_rate} onChange={set('conversion_rate')} placeholder="0" />
			</label>
			<label className="cop-form-field cop-form-span">
				<span>Recommended action</span>
				<input className="cop-input" value={form.recommended_action} onChange={set('recommended_action')} placeholder="How this group should be targeted" />
			</label>
			{error && <div className="cop-form-error cop-form-span">{error}</div>}
		</div>
	);
}

export default function ClusteringStudio() {
	const { navigate, isInternal, segments, createSegment, updateSegment, deleteSegment, reportDateRangeLabel } = useCop();
	const [filter, setFilter] = useState('all');
	const [modal, setModal] = useState(null); // { mode: 'create' | 'edit', id? }
	const [form, setForm] = useState(EMPTY_FORM);
	const [error, setError] = useState('');
	const [saving, setSaving] = useState(false);
	const [confirmDelete, setConfirmDelete] = useState(null);

	const filters = ['all', 'custom', 'active', 'monitoring', 'building'];
	const rows = segments.filter((s) => {
		if (filter === 'all') return true;
		if (filter === 'custom') return s.source === 'custom';
		return s.status === filter;
	});

	const openCreate = () => {
		setForm(EMPTY_FORM);
		setError('');
		setModal({ mode: 'create' });
	};

	const openEdit = (segment) => {
		setForm({
			segment_name: segment.segment_name,
			funnel_stage: segment.funnel_stage,
			recommended_solution_family: segment.recommended_solution_family,
			audience_size: segment.audience_size || '',
			avg_cart_value: segment.avg_cart_value || '',
			conversion_rate: segment.conversion_rate || '',
			recommended_action: segment.recommended_action || '',
		});
		setError('');
		setModal({ mode: 'edit', id: segment.segment_id });
	};

	const submit = () => {
		if (!form.segment_name.trim()) {
			setError('Segment name is required.');
			return;
		}
		if (!form.funnel_stage) {
			setError('Funnel stage is required.');
			return;
		}
		setSaving(true);
		// Brief async simulation so the save state is visible (the seam to /api/v1/cop/segments).
		setTimeout(() => {
			if (modal.mode === 'create') {
				createSegment(form);
			} else {
				updateSegment(modal.id, {
					segment_name: form.segment_name.trim(),
					funnel_stage: form.funnel_stage,
					recommended_solution_family: form.recommended_solution_family,
					audience_size: Number(form.audience_size) || 0,
					avg_cart_value: Number(form.avg_cart_value) || 0,
					conversion_rate: Number(form.conversion_rate) || 0,
					recommended_action: form.recommended_action,
				});
			}
			setSaving(false);
			setModal(null);
		}, 350);
	};

	const doDelete = () => {
		deleteSegment(confirmDelete.segment_id);
		setConfirmDelete(null);
	};

	return (
		<main className="cop-page">
			<div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
				<div style={{ flex: 1, minWidth: 280 }}>
					<h1 className="cop-page-title">Clustering Studio</h1>
					<div className="cop-page-sub">AI-powered behavioral segmentation · {reportDateRangeLabel} · last re-cluster 12 min ago</div>
				</div>
				<button className="cop-btn primary" onClick={openCreate}>+ New Custom Group</button>
			</div>

			<Card title="AI Segment Intelligence" icon="✦" right={`updated ${segmentIntelligence.updated}`} className="cop-brief">
				<p style={{ margin: '0 0 8px', color: 'var(--text)', fontWeight: 600, fontSize: 13 }}>
					{segmentIntelligence.headline}
				</p>
				<p style={{ margin: 0, color: 'var(--text-sec)', fontSize: 12.5, lineHeight: 1.65 }}>{segmentIntelligence.body}</p>
			</Card>

			<Card title="Behavioral Cluster Map" icon="✣" right="43,201 visitors · 6 clusters">
				<ClusterScatter />
			</Card>

			<Card
				title="Segments"
				icon="▤"
				right={
					<span className="cop-filter-row">
						{filters.map((f) => (
							<button
								key={f}
								className={`cop-filter-chip${filter === f ? ' active' : ''}`}
								onClick={() => setFilter(f)}
							>
								{f}
							</button>
						))}
					</span>
				}
			>
				<div className="cop-table-wrap">
				<table className="cop-table">
					<thead>
						<tr>
							<th>Segment</th>
							<th>Visitors</th>
							<th>Behavioral profile</th>
							<th>Avg cart</th>
							<th>Conv.</th>
							<th>Confidence</th>
							<th>Status</th>
							<th style={{ textAlign: 'right' }} />
						</tr>
					</thead>
					<tbody>
						{rows.map((s) => (
							<tr key={s.segment_id}>
								<td>
									<span className="cop-legend-dot" style={{ color: s.color, background: s.color }} />
									<b>{s.segment_name}</b>
									{s.source === 'custom' && <Pill tone="purple">custom</Pill>}
									<div style={{ fontSize: 10.5, color: 'var(--text-dim)', marginLeft: 14 }}>{s.recommended_action}</div>
								</td>
								<td className="cop-num">{s.audience_size.toLocaleString()}</td>
								<td>
									<div className="cop-profile-chips">
										{s.behavioral_profile.map((b) => (
											<span key={b}>{b}</span>
										))}
									</div>
								</td>
								<td className="cop-num">{s.avg_cart_value ? `$${s.avg_cart_value}` : '—'}</td>
								<td className="cop-num">{s.conversion_rate}%</td>
								<td>{s.confidence_score != null ? <Confidence value={s.confidence_score} /> : '—'}</td>
								<td><StatusPill status={s.status} /></td>
								<td>
									<div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
										{!s.is_system_managed && (
											<>
												<button className="cop-btn small" onClick={() => openEdit(s)}>Edit</button>
												<button className="cop-btn small danger" onClick={() => setConfirmDelete(s)}>Delete</button>
											</>
										)}
										<button
											className="cop-btn small"
											onClick={() => navigate('builder', { segment: s.segment_id })}
										>
											Launch →
										</button>
									</div>
								</td>
							</tr>
						))}
						{rows.length === 0 && (
							<tr>
								<td colSpan={8} style={{ textAlign: 'center', color: 'var(--text-dim)', padding: '18px 0' }}>
									No segments match this filter. Create a custom group to target an audience the recommendations did not surface.
								</td>
							</tr>
						)}
					</tbody>
				</table>
				</div>
			</Card>

			<div className="cop-banner">
				<div>
					<h3>Turn every segment into a personalized campaign — automatically.</h3>
					<p>
						Each segment ships with an AI-recommended tactic, offer logic, and trigger rules. Need a group the model
						didn't surface? Create a custom group and target it directly — campaigns still route through readiness
						checks and the approval queue before anything launches.
					</p>
				</div>
				<button className="cop-btn primary" onClick={() => navigate('builder')}>
					Launch a Targeted Campaign →
				</button>
			</div>

			{isInternal && (
				<InternalCard title="Technical Configuration & Model Diagnostics" icon="⚙" right="K-Means++ · 6 clusters · silhouette 0.61">
					<div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, fontSize: 12 }}>
						{[
							['Algorithm', 'K-Means++ with behavioral embedding'],
							['Refresh cadence', 'Every 6 hours'],
							['Feature window', '30 days, session aggregates'],
							['Min segment size', '500 visitors'],
							['Data source', 'Shopify analytics connection (hourly)'],
							['Suppression', 'consent-unknown excluded from activation'],
						].map(([k, v]) => (
							<div key={k} style={{ padding: '10px 12px', borderRadius: 9, background: 'var(--inset)', border: '1px solid var(--border)' }}>
								<div style={{ color: 'var(--text-dim)', fontSize: 10, textTransform: 'uppercase', letterSpacing: 1 }}>{k}</div>
								<div style={{ marginTop: 3, color: 'var(--text-sec)' }}>{v}</div>
							</div>
						))}
					</div>
					<div style={{ marginTop: 10 }}>
						{clusteringDiagnostics.warnings.map((w, i) => (
							<WarnRow key={i} level={w.level}>{w.text}</WarnRow>
						))}
					</div>
					<div className="cop-aop-links">
						<AopLink view="workflow-runs">segment_bot runs</AopLink>
						<AopLink view="audit-log">AOP audit</AopLink>
					</div>
				</InternalCard>
			)}

			{modal && (
				<Modal
					title={modal.mode === 'create' ? 'Create custom group' : 'Edit custom group'}
					sub="Custom groups let you target an audience the clustering recommendations did not surface. They route through the same readiness and approval gates."
					onClose={() => (saving ? null : setModal(null))}
					actions={
						<>
							<button className="cop-btn" onClick={() => setModal(null)} disabled={saving}>Cancel</button>
							<button className="cop-btn primary" onClick={submit} disabled={saving}>
								{saving ? 'Saving…' : modal.mode === 'create' ? 'Create group' : 'Save changes'}
							</button>
						</>
					}
				>
					<SegmentForm form={form} setForm={setForm} error={error} />
				</Modal>
			)}

			{confirmDelete && (
				<Modal
					title={`Delete “${confirmDelete.segment_name}”?`}
					sub="This removes the custom group. Recommendation-driven segments cannot be deleted."
					onClose={() => setConfirmDelete(null)}
					actions={
						<>
							<button className="cop-btn" onClick={() => setConfirmDelete(null)}>Cancel</button>
							<button className="cop-btn danger" onClick={doDelete}>Delete group</button>
						</>
					}
				>
					<div className="cop-check-row"><span style={{ color: 'var(--amber)' }}>!</span> Any drafts targeting this group will need a new audience.</div>
				</Modal>
			)}
		</main>
	);
}
